// <auto-generated>
// This file is automatically generated from DSL.
// Source: dsl/entities/OrgUnit.yaml
// Generated: 2025-12-22T14:00:02.344Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { orgUnitService } from '@/services/core/OrgUnitService';
import { CreateOrgUnit, UpdateOrgUnit, OrgUnit } from '@/types/core/OrgUnit';
import { authService } from '@/services/auth/authService';

/**
 * Query key factory for OrgUnit
 * Includes tenant ID to ensure different tenants have separate caches
 */
export const orgunitKeys = {
  all: (tenantId?: string | null) => ['orgunits', tenantId] as const,
  lists: (tenantId?: string | null) => [...orgunitKeys.all(tenantId), 'list'] as const,
  list: (tenantId?: string | null, filters?: any) => [...orgunitKeys.lists(tenantId), { filters }] as const,
  details: (tenantId?: string | null) => [...orgunitKeys.all(tenantId), 'detail'] as const,
  detail: (id: string, tenantId?: string | null) => [...orgunitKeys.details(tenantId), id] as const,
};

/**
 * Hook to fetch all OrgUnits
 */
export function useOrgUnits(options?: Omit<UseQueryOptions<OrgUnit[], Error>, 'queryKey' | 'queryFn'>) {
  // Get current tenant ID for cache key
  const tenantId = authService.getTenantId();

  return useQuery<OrgUnit[], Error>({
    queryKey: orgunitKeys.lists(tenantId),
    queryFn: () => orgUnitService.getAll(),
    ...options
  });
}

/**
 * Hook to fetch a single OrgUnit by ID
 */
export function useOrgUnit(
  id: string,
  options?: Omit<UseQueryOptions<OrgUnit | undefined, Error>, 'queryKey' | 'queryFn'>
) {
  const tenantId = authService.getTenantId();

  return useQuery<OrgUnit | undefined, Error>({
    queryKey: orgunitKeys.detail(id, tenantId),
    queryFn: () => orgUnitService.getById(id),
    enabled: !!id && id !== 'new',
    ...options
  });
}

/**
 * Hook to create a new OrgUnit
 */
export function useCreateOrgUnit() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<OrgUnit, Error, CreateOrgUnit, { previousOrgUnits?: OrgUnit[] }>({
    mutationFn: (data: CreateOrgUnit) => orgUnitService.create(data),
    onMutate: async (newOrgUnit) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: orgunitKeys.lists(tenantId) });

      // Snapshot previous value
      const previousOrgUnits = queryClient.getQueryData<OrgUnit[]>(orgunitKeys.lists(tenantId));

      // Optimistically update to the new value. Spread the input first so a
      // client-supplied id (offline / idempotency key) wins over the temp one.
      queryClient.setQueryData<OrgUnit[]>(orgunitKeys.lists(tenantId), (old = []) => [
        ...old,
        { ...newOrgUnit, id: (newOrgUnit as { id?: string }).id ?? 'temp-' + Date.now() } as OrgUnit
      ]);

      return { previousOrgUnits };
    },
    onError: (_err, _newOrgUnit, context) => {
      // Rollback on error
      if (context?.previousOrgUnits) {
        queryClient.setQueryData(orgunitKeys.lists(tenantId), context.previousOrgUnits);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: orgunitKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to update an existing OrgUnit
 */
export function useUpdateOrgUnit() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<OrgUnit, Error, { id: string; data: UpdateOrgUnit }, { previousOrgUnit?: OrgUnit; previousOrgUnits?: OrgUnit[] }>({
    mutationFn: ({ id, data }) => orgUnitService.update(id, data),
    onMutate: async ({ id, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: orgunitKeys.detail(id, tenantId) });
      await queryClient.cancelQueries({ queryKey: orgunitKeys.lists(tenantId) });

      // Snapshot previous values
      const previousOrgUnit = queryClient.getQueryData<OrgUnit>(orgunitKeys.detail(id, tenantId));
      const previousOrgUnits = queryClient.getQueryData<OrgUnit[]>(orgunitKeys.lists(tenantId));

      // Optimistically update detail
      queryClient.setQueryData<OrgUnit>(orgunitKeys.detail(id, tenantId), (old) => ({
        ...old!,
        ...data
      }) as OrgUnit);

      // Optimistically update list
      queryClient.setQueryData<OrgUnit[]>(orgunitKeys.lists(tenantId), (old = []) =>
        old.map((item) => (item.id === id ? { ...item, ...data } as OrgUnit : item))
      );

      return { previousOrgUnit, previousOrgUnits };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousOrgUnit) {
        queryClient.setQueryData(orgunitKeys.detail(id, tenantId), context.previousOrgUnit);
      }
      if (context?.previousOrgUnits) {
        queryClient.setQueryData(orgunitKeys.lists(tenantId), context.previousOrgUnits);
      }
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: orgunitKeys.detail(id, tenantId) });
      queryClient.invalidateQueries({ queryKey: orgunitKeys.lists(tenantId) });
    }
  });
}

/**
 * Hook to delete a OrgUnit
 */
export function useDeleteOrgUnit() {
  const queryClient = useQueryClient();
  const tenantId = authService.getTenantId();

  return useMutation<void, Error, string, { previousOrgUnits?: OrgUnit[] }>({
    mutationFn: (id: string) => orgUnitService.delete(id),
    onMutate: async (id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: orgunitKeys.lists(tenantId) });

      // Snapshot previous value
      const previousOrgUnits = queryClient.getQueryData<OrgUnit[]>(orgunitKeys.lists(tenantId));

      // Optimistically remove from list
      queryClient.setQueryData<OrgUnit[]>(orgunitKeys.lists(tenantId), (old = []) =>
        old.filter((item) => item.id !== id)
      );

      return { previousOrgUnits };
    },
    onError: (_err, _id, context) => {
      // Rollback on error
      if (context?.previousOrgUnits) {
        queryClient.setQueryData(orgunitKeys.lists(tenantId), context.previousOrgUnits);
      }
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: orgunitKeys.lists(tenantId) });
    }
  });
}

/**
 * Combined hook for save (create or update)
 * Automatically determines whether to create or update based on presence of ID
 */
export function useSaveOrgUnit() {
  const createMutation = useCreateOrgUnit();
  const updateMutation = useUpdateOrgUnit();

  return {
    mutate: (params: { id?: string; data: CreateOrgUnit | UpdateOrgUnit }, options?: any) => {
      if (params.id && params.id !== 'new') {
        return updateMutation.mutate({ id: params.id, data: params.data as UpdateOrgUnit }, options);
      } else {
        return createMutation.mutate(params.data as CreateOrgUnit, options);
      }
    },
    isPending: createMutation.isPending || updateMutation.isPending,
    isError: createMutation.isError || updateMutation.isError,
    error: createMutation.error || updateMutation.error
  };
}
